<template>
    <div class="tw-w-full tw-h-full tw-p-4">
        <!-- Loading State -->
        <div v-if="loading" class="tw-space-y-4">
            <!-- Loading skeleton for session cards -->
            <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-4">
                <div v-for="i in 8" :key="i" 
                    class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-shadow-sm tw-relative tw-overflow-hidden tw-min-h-[320px] tw-h-full tw-animate-pulse">
                    <!-- Status badge skeleton -->
                    <div class="tw-absolute tw-top-2 tw-right-2 tw-z-10 tw-px-2 tw-py-1 tw-bg-gray-200 tw-rounded-md tw-w-20 tw-h-6"></div>
                    
                    <!-- Session image skeleton -->
                    <div class="tw-relative tw-w-full" style="padding-top: 50%;">
                        <div class="tw-absolute tw-inset-0 tw-bg-gray-200"></div>
                    </div>

                    <!-- Session content skeleton -->
                    <div class="tw-p-3 tw-space-y-3">
                        <!-- Title skeleton -->
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-3/4"></div>
                        
                        <!-- Topic skeleton -->
                        <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-1/2"></div>
                        
                        <!-- Description skeleton -->
                        <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                        <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-2/3"></div>
                        
                        <!-- Meta skeleton -->
                        <div class="tw-space-y-2">
                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-1/3"></div>
                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-1/2"></div>
                        </div>
                        
                        <!-- Speakers skeleton -->
                        <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-2/3"></div>
                        
                        <!-- Actions skeleton -->
                        <div class="tw-flex tw-justify-between tw-items-center">
                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20"></div>
                            <div class="tw-flex tw-gap-2">
                                <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-16"></div>
                                <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-16"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Content -->
        <div v-else>
            <!-- Sessions Grid -->
            <div v-if="sessions.length > 0" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-4">
                <SessionCard 
                    v-for="session in sessions" 
                    :key="session.id" 
                    :session="session"
                    :show-join-button="true"
                    :loading="loadingSessions.has(session.id)"
                    @join="handleSessionJoin"
                    @view="handleSessionView"
                />
            </div>

            <!-- Empty State -->
            <div v-else class="tw-text-center tw-py-12 tw-min-h-[calc(100vh-300px)] tw-flex tw-items-center tw-justify-center">
                <div class="tw-max-w-md tw-mx-auto">
                    <div class="tw-w-24 tw-h-24 tw-mx-auto tw-mb-6 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                        <icon icon="solar:calendar-outline" class="tw-text-4xl tw-text-gray-400" />
                    </div>
                    <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">
                        {{ $t('messages.no-scheduled-sessions', 'No Scheduled Sessions') }}
                    </h3>
                    <p class="tw-text-gray-500 tw-mb-6">
                        {{ $t('messages.no-scheduled-sessions-description', 'You don\'t have any sessions scheduled yet. Explore available sessions and join the ones that interest you!') }}
                    </p>
                    <a-button as="router-link" :to="{ name: 'shared.agenda' }" intent="primary" left-icon="solar:calendar-bold-duotone">
                        {{ $t('buttons.explore-sessions', 'Explore Sessions') }}
                    </a-button>
                </div>
            </div>

            <!-- Pagination -->
            <div v-if="sessions.length > 0" class="tw-mt-8">
                <TablePagination 
                    :has-next="!!paginationOptions.next_page_url" 
                    :has-previous="!!paginationOptions.prev_page_url" 
                    @previous="onPrevious" 
                    @next="onNext" 
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import SessionCard from '@/components/sessions/SessionCard.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import { mySchedule } from '@/api/my-eshow';
import { join } from '@/api/sessions';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const sessions = ref([]);
const loadingSessions = ref(new Set());
const paginationOptions = ref({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0,
    next_page_url: null,
    prev_page_url: null,
    first_page_url: null,
    last_page_url: null,
    path: null,
    links: []
});

// Fetch scheduled sessions
const getScheduledSessions = async (page = 1) => {
    try {
        loading.value = true;
        const response = await mySchedule({ page });
        
        // Update sessions data
        sessions.value = response.data.data || [];
        
        // Update pagination options
        paginationOptions.value = {
            current_page: response.data.current_page || 1,
            last_page: response.data.last_page || 1,
            per_page: response.data.per_page || 10,
            total: response.data.total || 0,
            from: response.data.from || 0,
            to: response.data.to || 0,
            next_page_url: response.data.next_page_url,
            prev_page_url: response.data.prev_page_url,
            first_page_url: response.data.first_page_url,
            last_page_url: response.data.last_page_url,
            path: response.data.path,
            links: response.data.links || []
        };
    } catch (error) {
        console.error('Error fetching scheduled sessions:', error);
        useAlert(t('messages.error-fetching-scheduled-sessions', 'Error fetching scheduled sessions. Please try again.'), 'danger');
        sessions.value = [];
    } finally {
        loading.value = false;
    }
};

// Pagination handlers
const onNext = () => {
    if (paginationOptions.value.next_page_url) {
        const nextPage = paginationOptions.value.current_page + 1;
        getScheduledSessions(nextPage);
    }
};

const onPrevious = () => {
    if (paginationOptions.value.prev_page_url) {
        const prevPage = paginationOptions.value.current_page - 1;
        getScheduledSessions(prevPage);
    }
};

// Session event handlers
const handleSessionJoin = async (session) => {
    loadingSessions.value.add(session.id);
    try {
        // Call the join API to toggle join status
        await join(session.id);
        const action = session.is_joined ? 'left' : 'joined';
        useAlert(`Successfully ${action} "${session.name}"`, 'success');
        
        // Update the session's join status in local state
        const sessionIndex = sessions.value.findIndex(s => s.id === session.id);
        if (sessionIndex !== -1) {
            sessions.value[sessionIndex].is_joined = !sessions.value[sessionIndex].is_joined;
        }
    } catch (error) {
        console.error('Error toggling session join:', error);
        
        // Handle specific 422 error (session has ended)
        if (error.response && error.response.status === 422) {
            const errorMessage = error.response.data?.message || 'This session has already ended.';
            useAlert(errorMessage, 'warning');
        } else {
            const action = session.is_joined ? 'leave' : 'join';
            useAlert(`Failed to ${action} session. Please try again.`, 'danger');
        }
    } finally {
        loadingSessions.value.delete(session.id);
    }
};

const handleSessionView = (session) => {
    useAlert(`Viewing details for "${session.name}"`, 'info');
    // In a real implementation, you might navigate to a detail page
    console.log('View session:', session);
};

// Initialize on mount
onMounted(() => {
    getScheduledSessions();
});
</script>

<style scoped>
/* Add any custom styles here */
</style>